<template>
  <button
    v-if="active"
    :class="'chatbotToggle' + (open ? ' open' : '')"
    :aria-label="$t('chatbot.toggle_label')"
    :aria-expanded="open ? 'true' : 'false'"
    @click="openChatBot"
    ref="toggle"
  >
    <div
      v-tooltip="{
        title: 'Léa, votre assistante IA',
        position: 'left',
      }"
      aria-hidden="true"
    >
      <img
        class="chatbot__logo"
        :class="{ show: !buttonHover && (!animatedLogo || open) }"
        src="/assets/images/symbole_small.png"
        alt=""
      />
      <div class="loader-app chatbotLoader" :class="{ show: buttonHover || (animatedLogo && !open) }">
        <div class="purple1 -fade--1"></div>
        <div class="purple2 -fade--2"></div>
        <div class="green -fade--3"></div>
        <div class="red -fade--4"></div>
      </div>
    </div>
    <div class="chatbot__bulle" v-if="bulle" aria-hidden="true">
      <img src="/assets/images/illustration.png" alt="" />
      Léa a une information pour vous !
    </div>
  </button>
  <div v-if="active" class="chatbot" :class="{ open: open }">
    <div
      class="blockStyle2 h-100"
      role="dialog"
      :aria-modal="open ? 'true' : 'false'"
      :aria-label="$t('chatbot.panel_label')"
    >
      <div class="chatbot__heading">
        <img class="chatbot__logo show" src="/assets/images/illustration.png" alt="" />
        <h2 class="textStyle mt-3">LEA</h2>
        <p class="textStyle">Votre assistante IA</p>
        <button class="chatbot__close" :aria-label="$t('chatbot.close_label')" @click="closeChatBot">
          <FontAwesomeIcon icon="fa-light fa-xmark" aria-hidden="true" />
        </button>
      </div>
      <div ref="chatMessages" class="chatbot__messages" aria-live="polite" aria-atomic="false">
        <div
          v-for="message in messages"
          :key="message.id"
          :class="'chatbot__row ' + (message.writer === 'me' ? 'me' : 'bot')"
        >
          <div class="chatbot__message">
            <!-- Messages utilisateur : texte brut (jamais v-html) pour éviter toute injection.
                 Réponses du bot : HTML déjà sanitizé côté backend (ChatbotMarkdownRenderer). -->
            <div v-if="message.writer === 'me'" class="chatbot__text">
              {{ message.content }}
            </div>
            <div v-else class="chatbot__text text-typing" v-html="message.content"></div>
          </div>
        </div>
        <div v-if="loading" class="chatbot__row bot" :aria-label="$t('chatbot.loading_label')">
          <div class="chatbot__message loading">
            <div class="chatbot__typing">
              <div class="dot-typing"></div>
            </div>
          </div>
        </div>
      </div>
      <form class="chatbot__form" @submit="submit($event)">
        <ModelComponent :model="model" :disableFocusAnimation="true" />
        <button
          class="chatbot__submit"
          :class="{ '--stop': canStop }"
          type="submit"
          :aria-label="canStop ? $t('chatbot.stop_label') : $t('chatbot.send_label')"
        >
          <FontAwesomeIcon :icon="'fa-light fa-' + (canStop ? 'stop' : 'arrow-up')" aria-hidden="true" />
        </button>
      </form>
    </div>
  </div>
</template>

<script>
import { mapState } from "pinia";
import { mapActions } from "pinia";
import { useChatbotStore } from "@/store/chatbot/chatbot";
import ModelComponent from "@/components/form/ModelComponent.vue";

export default {
  name: "ChatBotComponent",
  data() {
    return {
      model: {
        vars: {
          block_prefixes: ["", "text", "textarea"],
          name: "user_chatbot",
          label: false,
          id: "user_chatbot",
          value: "",
          required: false,
          attr: {
            placeholder: "Posez votre question...",
            autocomplete: "off",
          },
          row_attr: {
            class: "m-0",
          },
        },
      },
      open: false,
      active: true,
      interval: null,
      intervalLength: 10,
      animatedLogo: false,
      intervalLogo: null,
      intervalLogoLength: 5000,
      buttonHover: false,
      step: 0,
      bulle: false,
    };
  },
  computed: {
    ...mapState(useChatbotStore, {
      messages: (store) => store.messages,
      loading: (store) => store.loading,
      canStop: (store) => store.canStop,
    }),
  },
  mounted() {
    this.setLogoInterval();
    this.initLinkEvents();
    const backdrop = document.getElementById("backdropChatbot");
    backdrop.classList.remove("show");
    backdrop.addEventListener("click", () => {
      this.closeChatBot();
    });
    this.$refs.toggle.addEventListener("mouseover", () => {
      this.buttonHover = true;
    });
    this.$refs.toggle.addEventListener("mouseout", () => {
      this.buttonHover = false;
    });
    const input = document.getElementById("user_chatbot");
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        this.submit(e);
      }
    });
  },

  methods: {
    submit(e) {
      e.preventDefault();
      if (this.canStop) {
        this.stop(this.model);
      } else {
        this.post(e);
      }
    },

    initLinkEvents() {
      this.$refs.chatMessages.addEventListener("click", (e) => {
        e.preventDefault();
        let target = e.target;
        if (target.tagName !== "A") {
          return;
        }
        if (target.href === location.href) {
          this.closeChatBot();
          return;
        }

        if (target.tagName === "A") {
          let url = null;
          try {
            url = new URL(target.href);
          } catch (err) {
            return;
          }

          const to = { path: url.pathname, query: this.$route.query };
          if (url.hash && window.location.pathname === to) {
            return;
          }

          this.$router.push(to);
          this.closeChatBot();
        }
      });
    },

    post(e) {
      e.preventDefault();
      const $this = this;
      const container = $this.$refs.chatMessages;
      if (this.model.vars.value) {
        this.model.vars.disabled = true;
        this.setCanStop(true);
        const message = {
          content: this.model.vars.value,
          writer: "me",
        };

        $this.storeMessage(message, container);
        $this.chatbotRequest(message, container, this.model).catch(() => {});

        this.model.vars.value = "";
      }
    },

    setLogoInterval() {
      if (this.intervalLogo) {
        clearInterval(this.intervalLogo);
      }
      this.intervalLogo = setInterval(() => {
        this.animatedLogo = !this.animatedLogo;
      }, this.intervalLogoLength);
    },

    focusInput() {
      const input = document.getElementById("user_chatbot");
      setTimeout(() => {
        input.focus();
      }, 50);
    },

    openChatBot() {
      this.open = true;
      const container = this.$refs.chatMessages;
      container.scrollTop = container.scrollHeight;
      const backdrop = document.getElementById("backdropChatbot");
      backdrop.classList.add("show");
      this.focusInput();
      this.bulle = false;
      this.loadUserHistoric(container);
    },

    closeChatBot() {
      this.open = false;
      const backdrop = document.getElementById("backdropChatbot");
      backdrop.classList.remove("show");
    },

    ...mapActions(useChatbotStore, {
      storeMessage: "storeMessage",
      chatbotSuccess: "chatbotSuccess",
      chatbotRequest: "chatbotRequest",
      writeContent: "writeContent",
      chatbotLoading: "chatbotLoading",
      clear: "clear",
      loadUserHistoric: "loadUserHistoric",
      setCanStop: "setCanStop",
      stop: "stop",
    }),
  },
  components: {
    ModelComponent,
  },
};
</script>

<style lang="scss">
$chatbot-desktop-width: 30%;

.chatbotToggle {
  width: 60px;
  height: 60px;
  position: fixed;
  bottom: 170px;
  right: 0;
  font-weight: 700;
  background: $white;
  box-shadow: rgba(0, 0, 0, 0.1) 0 5px 15px;
  border-radius: $radius 0 0 $radius;
  border: 2px solid $lighter-grey;
  color: $blue;
  padding: 0 8px;
  display: flex;
  align-items: center;
  font-size: 15px;
  justify-content: space-between;
  z-index: 1058;
  transition: all 300ms ease-in-out;

  img {
    width: 50px;
  }

  // Ouvert : le toggle suit le bord gauche du panneau (30%) au lieu d'être recouvert
  &.open {
    right: $chatbot-desktop-width;
  }

  @include up-to-lg {
    bottom: 20px;

    // Mobile : panneau pleine largeur, pas de place à côté → toggle reste au bord
    &.open {
      right: 0;
    }
  }

  @include dark-theme {
    background: $m-color_12;
    border-color: $m-color_9;
    color: $dark-text;
  }
}

.chatbot {
  position: fixed;
  right: 0;
  bottom: 0;
  top: 0;
  width: $chatbot-desktop-width;
  max-width: 100%;
  transition: all 300ms ease-in-out;
  z-index: 1059;
  transform: translateX(100%);
  box-shadow: rgba(0, 0, 0, 0.1) 0 5px 15px;

  @include up-to-lg {
    width: 100%;
  }

  &.open {
    transform: translateX(0);
  }

  textarea {
    border-radius: 6px;
    border: 1px solid var(--primary-color);
    max-height: 50px;
    padding-right: 50px;
    resize: none;
    height: auto;
    min-height: 100px;

    &:focus,
    &:active {
      border: 1px solid var(--primary-color);
    }
  }

  &__heading {
    background: $white;
    height: 192px;
    width: 100%;
    padding: 15px;
    position: relative;
    text-align: center;
    border-bottom: 1px dashed var(--primary-color);

    h2 {
      height: auto;
      margin-top: 0;
    }
  }

  &__logo {
    width: 90px;
    position: relative;
    top: 2px;
    left: -6px;
    opacity: 0;
    visibility: visible;
    transition: all 300ms ease-in-out;

    &.show {
      visibility: visible;
      opacity: 1;
    }
  }

  &__close {
    position: absolute;
    top: 20px;
    transform: translateY(-50%);
    right: 10px;
    background: none;
    border: none;
    color: $black;
    font-size: 22px;
  }

  &__writer {
    font-weight: 700;
  }

  &__form {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: $white;
    padding: 15px;
    border-top: 1px dashed var(--primary-color);
  }

  &__messages {
    padding: 25px 15px;
    height: calc(100% - 323px);
    overflow: scroll;
    background: $white;
    position: relative;
  }

  &__text {
    &.-short {
      overflow: hidden;
      display: -webkit-box;
      -webkit-line-clamp: 15;
      line-clamp: 15;
      -webkit-box-orient: vertical;
    }
  }

  &__row {
    display: flex;
    justify-content: flex-end;

    &.bot {
      justify-content: flex-start;

      .chatbot__message {
        background: $white;
        color: $dark;
        border: 1px solid $m_color_3;

        a,
        button {
          background: var(--primary-color);
          color: $white;
        }
      }
    }
  }

  &__message {
    background: var(--primary-color);
    color: $white;
    border-radius: 10px;
    padding: 10px;
    width: 80%;

    &.loading {
      max-width: 45px;
      height: 25px;
    }

    a,
    button {
      background: $white;
      color: $blue;
      margin-top: 10px;
      border-radius: $radius;
      padding: 5px 8px;
      border: none;
      display: block;
      text-align: left;
    }

    .d-inline-block + .d-inline-block {
      margin-left: 8px;
    }

    div:not([class]) {
      margin-bottom: 10px;
    }
  }

  &__typing {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
  }

  &__row + &__row {
    margin-top: 10px;
  }

  &__submit {
    position: absolute;
    right: 25px;
    bottom: 25px;
    border: none;
    width: 100%;
    height: 100%;
    max-width: 25px;
    max-height: 25px;
    border-radius: 50%;
    background: var(--primary-color);
    color: $white;
  }

  &__bulle {
    position: absolute;
    padding: 15px;
    border-radius: 15%;
    top: 50%;
    transform: translateY(-50%);
    right: 90px;
    background: $white;
    box-shadow: rgba(0, 0, 0, 0.1) 0 5px 15px;
    text-align: center;
    width: 180px;

    &:before {
      content: "";
      position: absolute;
      right: 0;
      top: 50%;
      transform: rotate(45deg) translateY(-50%);
      background: $white;
      width: 20px;
      height: 20px;
    }

    img {
      display: block;
      margin: auto;
      margin-bottom: 10px;
    }
  }
}

/**
 * ==============================================
 * Dot Typing
 * ==============================================
 */
.dot-typing {
  position: relative;
  left: -9999px;
  width: 4px;
  height: 4px;
  border-radius: 5px;
  background-color: $white;
  color: $white;
  box-shadow:
    9990px 0 0 0 $white,
    9999px 0 0 0 $white,
    10008px 0 0 0 $white;
  animation: dot-typing 1.5s infinite linear;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    box-shadow:
      9990px 0 0 0 $white,
      9999px 0 0 0 $white,
      10008px 0 0 0 $white;
  }
}

@keyframes dot-typing {
  0% {
    box-shadow:
      9990px 0 0 0 $white,
      9999px 0 0 0 $white,
      10008px 0 0 0 $white;
  }
  16.667% {
    box-shadow:
      9990px -5px 0 0 $white,
      9999px 0 0 0 $white,
      10008px 0 0 0 $white;
  }
  33.333% {
    box-shadow:
      9990px 0 0 0 $white,
      9999px 0 0 0 $white,
      10008px 0 0 0 $white;
  }
  50% {
    box-shadow:
      9990px 0 0 0 $white,
      9999px -5px 0 0 $white,
      10008px 0 0 0 $white;
  }
  66.667% {
    box-shadow:
      9990px 0 0 0 $white,
      9999px 0 0 0 $white,
      10008px 0 0 0 $white;
  }
  83.333% {
    box-shadow:
      9990px 0 0 0 $white,
      9999px 0 0 0 $white,
      10008px -5px 0 0 $white;
  }
  100% {
    box-shadow:
      9990px 0 0 0 $white,
      9999px 0 0 0 $white,
      10008px 0 0 0 $white;
  }
}

.stop {
  width: 25px;
  height: 25px;
  border-radius: 25px;
  background-color: $blue;
  color: $white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
